import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import QueueIcon from '@mui/icons-material/Queue';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { useBankDocumentQueuePage } from '../Hooks/useBankDocumentQueuePage';
import AssignEntryDialog from '../Components/AssignEntryDialog';
import QueueAllocationPreview from '../Components/QueueAllocationPreview';
import { useGetBankDocumentCompaniesLookupQuery } from '../Service/companiesLookupApi';
import { useGetBankDocumentBankAccountsLookupQuery } from '../Service/bankAccountsLookupApi';
import { BankDocumentsRoutes, BookingsRoutes, ExpenseInvoicesRoutes } from '../../../Router/path';
import {
  buildBankDocumentAllocationSummary,
  formatBankDocumentAllocationSummaryLabel,
} from '../Helpers/allocationSummary';
import {
  buildCreateBookingRouteFromBankDocumentContext,
  buildCreateReservationRouteFromBankDocumentContext,
  parseBankDocumentReservationCreateQuery,
} from '../../Shared/Helpers/bankDocumentReservationCreateNavigation';
import type { BankDocumentReservationCreateContext } from '../../Shared/Types/bankDocumentReservationCreate.types';
import type {
  BankDocumentEntryQueueDto,
  BankDocumentQueueTargetType,
} from '../Types/bankDocument.types';
import {
  isQueueEntryReadyToPost,
  resolveBankDocumentQueueState,
  resolveQueueStatusColor,
  resolveQueueStatusLabel,
} from '../Mapping/bankDocumentQueueStatusMapper';

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('pl-PL');
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return `${parsedDate.toLocaleDateString('pl-PL')} ${parsedDate.toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const formatAmount = (amount: number, currency?: string | null): string =>
  `${amount.toFixed(2)} ${currency ?? 'PLN'}`;

const normalizeSupplierMatchStatus = (status?: string | null): string | null => {
  if (!status) {
    return null;
  }

  if (status === 'MatchedByNip') {
    return 'MatchedByApContractor';
  }

  if (status === 'NoCompanyByNip') {
    return 'NoApContractorByNip';
  }

  if (status === 'InactiveCompany') {
    return 'InactiveApContractor';
  }

  return status;
};

const resolveSupplierMatchStatusLabel = (status?: string | null): string | null => {
  const normalizedStatus = normalizeSupplierMatchStatus(status);
  if (!normalizedStatus) {
    return null;
  }

  if (normalizedStatus === 'MatchedByApContractor') {
    return 'Dopasowano AP kontrahenta po NIP';
  }

  if (normalizedStatus === 'NoApContractorByNip') {
    return 'Brak AP kontrahenta po NIP';
  }

  if (normalizedStatus === 'InactiveApContractor') {
    return 'AP kontrahent nieaktywny';
  }

  if (normalizedStatus === 'NipConflict') {
    return 'Konflikt NIP (wiele dopasowań)';
  }

  return normalizedStatus;
};

const isSupplierMatchUnresolved = (status?: string | null): boolean => {
  const normalizedStatus = normalizeSupplierMatchStatus(status);

  return (
    normalizedStatus === 'NoApContractorByNip' ||
    normalizedStatus === 'InactiveApContractor' ||
    normalizedStatus === 'NipConflict'
  );
};

const resolveTarget = (
  invoiceId?: number | null,
  reservationId?: number | null,
  expenseInvoiceId?: number | null
): string => {
  if (invoiceId) {
    return `Faktura #${invoiceId}`;
  }

  if (reservationId) {
    return `Rezerwacja #${reservationId}`;
  }

  if (expenseInvoiceId) {
    return `FV kosztowa #${expenseInvoiceId}`;
  }

  return '-';
};

const buildReservationBookingTargetUrl = (
  bookingId: number,
  reservationId: number
): string => {
  const queryParams = new URLSearchParams({
    origin: 'bank-documents',
    focusReservationId: reservationId.toString(),
    returnTo: BankDocumentsRoutes.queue,
  });

  return `${BookingsRoutes.update.replace(':bookingId', bookingId.toString())}?${queryParams.toString()}`;
};

const buildQueueAllocationSummary = (
  item: BankDocumentEntryQueueDto
) =>
  buildBankDocumentAllocationSummary({
    amount: item.amount,
    invoiceId: item.invoiceId,
    reservationId: item.reservationId,
    allocationCount: item.allocationCount,
    allocatedAmount: item.allocatedAmount,
    remainingAmount: item.remainingAmount,
    allocationTypeSummary: item.allocationTypeSummary,
  });

const shouldRenderAllocationSummary = (
  item: BankDocumentEntryQueueDto,
  allocationSummary: ReturnType<typeof buildQueueAllocationSummary>
): boolean => {
  if (item.expenseInvoiceId || allocationSummary.allocationCount === 0) {
    return false;
  }

  return (
    allocationSummary.allocationCount > 1 ||
    allocationSummary.remainingAmount > 0 ||
    (!item.invoiceId && !item.reservationId)
  );
};

const renderQueueTargetDetails = (item: BankDocumentEntryQueueDto): React.ReactElement => {
  const allocationSummary = buildQueueAllocationSummary(item);
  const reservationId = item.reservationId;
  const bookingId = item.bookingId;

  if (!shouldRenderAllocationSummary(item, allocationSummary)) {
    if (
      typeof reservationId === 'number' &&
      reservationId > 0 &&
      typeof bookingId === 'number' &&
      bookingId > 0
    ) {
      const targetUrl = buildReservationBookingTargetUrl(bookingId, reservationId);

      return (
        <Link
          component={RouterLink}
          to={targetUrl}
          underline="hover"
          variant="body2"
          sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}
        >
          {`Rezerwacja #${item.reservationId}`}
        </Link>
      );
    }

    return <>{resolveTarget(item.invoiceId, item.reservationId, item.expenseInvoiceId)}</>;
  }

  const legacyTargetLabel = item.invoiceId
    ? `Faktura #${item.invoiceId}`
    : item.reservationId
      ? `Rezerwacja #${item.reservationId}`
      : item.expenseInvoiceId
        ? `FV kosztowa #${item.expenseInvoiceId}`
      : null;

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {formatBankDocumentAllocationSummaryLabel(allocationSummary)}
      </Typography>
      {(allocationSummary.allocationCount > 1 || allocationSummary.remainingAmount > 0) && (
        <Typography variant="caption" color="text.secondary">
          Przypisano {formatAmount(allocationSummary.allocatedAmount, item.currency)}, pozostało{' '}
          {formatAmount(allocationSummary.remainingAmount, item.currency)}
        </Typography>
      )}
      {legacyTargetLabel && (
        <Typography variant="caption" color="text.secondary">
          {legacyTargetLabel}
        </Typography>
      )}
      {allocationSummary.allocationTypeSummary === 'reservation' &&
        allocationSummary.allocationCount > 1 && (
          <QueueAllocationPreview
            bankDocumentId={item.bankDocumentId}
            entryId={item.entryId}
            allocationCount={allocationSummary.allocationCount}
          />
        )}
    </Stack>
  );
};

const BankDocumentQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnContext = React.useMemo(() => {
    const parsed = parseBankDocumentReservationCreateQuery(searchParams);
    if (!parsed || !parsed.reservationId) {
      return null;
    }
    return {
      entryId: parsed.entryId,
      bankDocumentId: parsed.bankDocumentId,
      reservationId: parsed.reservationId,
    };
  }, [searchParams]);

  React.useEffect(() => {
    if (returnContext) {
      navigate(window.location.pathname, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    queueItems,
    isLoading,
    isError,
    errorMessage,
    successMessage,
    filters,
    setFilter,
    pageNumber,
    setPageNumber,
    totalCount,
    totalPages,
    postingDescription,
    setPostingDescription,
    isProcessing,
    handlePostEntry,
    handleUnpostEntry,
    handlePostReadySubset,
    handleOpenAssignDialog,
    handleCloseAssignDialog,
    handleAssignSave,
    editEntry,
    editEntryCompanyId,
    initialDraftReservationId,
    refetch,
  } = useBankDocumentQueuePage({ returnContext });

  const { companies } = useGetBankDocumentCompaniesLookupQuery();
  const { accounts } = useGetBankDocumentBankAccountsLookupQuery(filters.companyId);

  const resolveReservationCreateContext = (): BankDocumentReservationCreateContext | null => {
    if (!editEntry) {
      return null;
    }

    return {
      origin: 'bank-documents',
      entryId: editEntry.id,
      bankDocumentId: editEntry.bankDocumentId,
      companyId: editEntryCompanyId ?? filters.companyId ?? null,
      returnTo: BankDocumentsRoutes.queue,
    };
  };

  const handleStartReservationQuickCreateFromBooking = (bookingId: number) => {
    const reservationCreateContext = resolveReservationCreateContext();
    if (!reservationCreateContext || !Number.isInteger(bookingId) || bookingId <= 0) {
      return;
    }

    handleCloseAssignDialog();
    navigate(buildCreateReservationRouteFromBankDocumentContext(reservationCreateContext, bookingId));
  };

  const handleStartReservationQuickCreateWithNewBooking = () => {
    const reservationCreateContext = resolveReservationCreateContext();
    if (!reservationCreateContext) {
      return;
    }

    handleCloseAssignDialog();
    navigate(buildCreateBookingRouteFromBankDocumentContext(reservationCreateContext));
  };

  return (
    <Stack spacing={3}>
      <CustomCard
        icon={<QueueIcon color="primary" fontSize="large" />}
        title="Kolejka operacyjna"
        subheader="Preset: ostatnie 7 dni + niezaksięgowane + błędy"
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Filtry globalne
        </Typography>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Data od"
              type="date"
              size="small"
              value={filters.dateFrom}
              onChange={(event) => setFilter('dateFrom', event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Data do"
              type="date"
              size="small"
              value={filters.dateTo}
              onChange={(event) => setFilter('dateTo', event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="queue-company-filter">Firma</InputLabel>
              <Select
                labelId="queue-company-filter"
                value={filters.companyId ?? ''}
                label="Firma"
                onChange={(event) => {
                  const value = event.target.value;
                  setFilter('companyId', value ? Number(value) : undefined);
                  setFilter('bankAccountId', undefined);
                }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="queue-account-filter">Konto bankowe</InputLabel>
              <Select
                labelId="queue-account-filter"
                value={filters.bankAccountId ?? ''}
                label="Konto bankowe"
                onChange={(event) => {
                  const value = event.target.value;
                  setFilter('bankAccountId', value ? Number(value) : undefined);
                }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Kontrahent"
              size="small"
              value={filters.counterpartyName ?? ''}
              onChange={(event) =>
                setFilter('counterpartyName', event.target.value || undefined)
              }
              sx={{ minWidth: 220 }}
            />
            <TextField
              label="Tytuł transakcji"
              size="small"
              value={filters.title ?? ''}
              onChange={(event) => setFilter('title', event.target.value || undefined)}
              sx={{ minWidth: 220 }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="queue-target-filter">Typ celu</InputLabel>
              <Select
                labelId="queue-target-filter"
                value={filters.targetType ?? ''}
                label="Typ celu"
                onChange={(event) => {
                  const value = event.target.value;
                  setFilter(
                    'targetType',
                    value ? (value as BankDocumentQueueTargetType) : undefined
                  );
                }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                <MenuItem value="reservation">Rezerwacja</MenuItem>
                <MenuItem value="invoice">Faktura</MenuItem>
                <MenuItem value="expenseInvoice">FV kosztowa</MenuItem>
                <MenuItem value="unassigned">Brak przypisania</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="queue-direction-filter">Kierunek</InputLabel>
              <Select
                labelId="queue-direction-filter"
                value={filters.amountDirection ?? ''}
                label="Kierunek"
                onChange={(event) => {
                  const value = event.target.value;
                  setFilter('amountDirection', value ? (value as 'inflow' | 'outflow') : undefined);
                }}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                <MenuItem value="inflow">Wpływ</MenuItem>
                <MenuItem value="outflow">Wypływ</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="queue-diagnostic-filter">Diagnostyka</InputLabel>
              <Select
                labelId="queue-diagnostic-filter"
                value={filters.diagnosticFilter}
                label="Diagnostyka"
                onChange={(event) =>
                  setFilter('diagnosticFilter', event.target.value as 'failed' | 'posted' | 'all')
                }
              >
                <MenuItem value="failed">Tylko błędy</MenuItem>
                <MenuItem value="posted">Tylko zaksięgowane</MenuItem>
                <MenuItem value="all">Wszystkie</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="queue-remainder-filter">Nieprzypisana reszta</InputLabel>
              <Select
                labelId="queue-remainder-filter"
                value={filters.withUnassignedRemainder ? 'withRemainder' : 'all'}
                label="Nieprzypisana reszta"
                onChange={(event) =>
                  setFilter('withUnassignedRemainder', event.target.value === 'withRemainder')
                }
              >
                <MenuItem value="all">Wszystkie</MenuItem>
                <MenuItem value="withRemainder">Tylko z nieprzypisaną resztą</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Akcje globalne
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Opis księgowania"
            size="small"
            fullWidth
            value={postingDescription}
            onChange={(event) => setPostingDescription(event.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            onClick={handlePostReadySubset}
            disabled={isProcessing || isLoading}
          >
            Zaksięguj gotowe (batch)
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Odśwież
          </Button>
        </Stack>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {(errorMessage || isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage ?? 'Nie udało się pobrać kolejki operacyjnej.'}
          </Alert>
        )}

        {filters.diagnosticFilter === 'failed' && !isLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Widok „Tylko błędy” pokazuje elementy z bieżącej strony danych.
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && queueItems.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Brak pozycji dla aktywnych filtrów.
          </Typography>
        )}

        {!isLoading && queueItems.length > 0 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Kwota</TableCell>
                    <TableCell>Kontrahent / Tytuł</TableCell>
                    <TableCell>Cel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Źródło</TableCell>
                    <TableCell align="center">Akcje wiersza</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueItems.map((item) => {
                    const queueState = resolveBankDocumentQueueState(item);
                    const isReadyToPost = isQueueEntryReadyToPost(item);

                    return (
                      <TableRow key={item.entryId} hover>
                        <TableCell>{formatDate(item.bookingDate)}</TableCell>
                        <TableCell>{formatAmount(item.amount, item.currency)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{item.counterpartyName ?? '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.title ?? '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {renderQueueTargetDetails(item)}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip
                              size="small"
                              color={resolveQueueStatusColor(queueState)}
                              label={resolveQueueStatusLabel(queueState, item.isPosted)}
                            />
                            {queueState.diagnosticCode && (
                              <Typography variant="caption" color="text.secondary">
                                {queueState.diagnosticCode}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                navigate(
                                  `${BankDocumentsRoutes.details.replace(
                                    ':bankDocumentId',
                                    item.bankDocumentId.toString()
                                  )}#bank-document-entry-row-${item.entryId}`
                                )
                              }
                            >
                              {item.fileName}
                            </Button>
                            {item.sourceSystem && (
                              <Typography variant="caption" color="text.secondary">
                                Źródło: {item.sourceSystem}
                              </Typography>
                            )}
                            {resolveSupplierMatchStatusLabel(item.supplierMatchStatus) && (
                              <Typography variant="caption" color="text.secondary">
                                Match: {resolveSupplierMatchStatusLabel(item.supplierMatchStatus)}
                                {item.supplierDraftId ? ` (Draft #${item.supplierDraftId})` : ''}
                              </Typography>
                            )}
                            {queueState.hasUnassignedRemainder &&
                              (item.createdBy || item.createdAtUtc) && (
                                <Typography variant="caption" color="text.secondary">
                                  Audit reszty: {item.createdBy ?? '-'} · {formatDateTime(item.createdAtUtc)}
                                </Typography>
                              )}
                            {item.supplierDraftId && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                  navigate(
                                    ExpenseInvoicesRoutes.edit.replace(
                                      ':id',
                                      item.supplierDraftId!.toString()
                                    )
                                  )
                                }
                              >
                                Otwórz draft FV kosztowej
                              </Button>
                            )}
                            {isSupplierMatchUnresolved(item.supplierMatchStatus) &&
                              !item.isPosted && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    void handleOpenAssignDialog(item);
                                  }}
                                  disabled={isProcessing}
                                >
                                  Wybierz AP kontrahenta
                                </Button>
                              )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {!item.isPosted && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                  void handleOpenAssignDialog(item);
                                }}
                                disabled={isProcessing}
                              >
                                Przypisz
                              </Button>
                            )}
                            {item.isPosted ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<UndoIcon />}
                                onClick={() => handleUnpostEntry(item.entryId, item.bankDocumentId)}
                                disabled={isProcessing}
                              >
                                Cofnij
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handlePostEntry(item.entryId)}
                                disabled={isProcessing || !isReadyToPost}
                              >
                                Zaksięguj
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Łącznie: {totalCount}
              </Typography>
              <Pagination
                color="primary"
                page={pageNumber}
                count={Math.max(totalPages, 1)}
                onChange={(_, value) => setPageNumber(value)}
              />
            </Stack>
          </>
        )}
      </CustomCard>

      <AssignEntryDialog
        open={Boolean(editEntry)}
        entry={editEntry}
        saving={isProcessing}
        companyId={editEntryCompanyId ?? filters.companyId}
        draftReservationId={initialDraftReservationId ?? undefined}
        onStartReservationQuickCreateFromBooking={handleStartReservationQuickCreateFromBooking}
        onStartReservationQuickCreateWithNewBooking={handleStartReservationQuickCreateWithNewBooking}
        onClose={handleCloseAssignDialog}
        onSave={handleAssignSave}
      />
    </Stack>
  );
};

export default BankDocumentQueuePage;
